import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { COMPANY, PROJECT_TYPES } from "@/lib/site-data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  project_type: PROJECT_TYPES[0],
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSuccess(true);
      setForm(INITIAL);
      toast.success("Inquiry received. We'll be in touch within 1 business day.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please review the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 bg-[var(--bg-secondary)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-20">
        <div className="md:col-span-5 reveal">
          <div className="flex items-center gap-4 mb-6">
            <span className="hairline w-10" />
            <span className="eyebrow">Start A Project</span>
          </div>
          <h2 className="font-display text-5xl md:text-6xl leading-[1.02] tracking-tight mb-8">
            Let's <em className="text-[var(--accent)]">draw</em> something
            worth building.
          </h2>
          <p className="text-[var(--text-secondary)] text-lg leading-relaxed mb-12 max-w-md">
            Tell us about your site, your vision, and your timeline. We'll
            reply personally within one business day.
          </p>

          <div className="space-y-6">
            <InfoRow label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testid="contact-email" />
            <InfoRow label="Office" value={COMPANY.phone} href={`tel:${COMPANY.phone.replace(/\D/g, "")}`} testid="contact-phone" />
            <InfoRow label="Mobile" value={COMPANY.phoneMobile} href={`tel:${COMPANY.phoneMobile.replace(/\D/g, "")}`} testid="contact-phone-mobile" />
            <InfoRow label="Studio" value={COMPANY.address} testid="contact-address" />
            <InfoRow label="Service Area" value={COMPANY.serviceArea} testid="contact-service-area" />
          </div>
        </div>

        <div className="md:col-span-7 reveal reveal-delay-1">
          {success ? (
            <div
              className="border border-[var(--accent)]/40 p-10 md:p-14 h-full flex flex-col justify-center text-center md:text-left"
              data-testid="contact-success"
            >
              <span className="eyebrow mb-6">Inquiry Received</span>
              <h3 className="font-display text-4xl md:text-5xl leading-tight mb-6">
                Thank you.
                <br />
                <em className="text-[var(--accent)]">
                  We'll be in touch shortly.
                </em>
              </h3>
              <p className="text-[var(--text-secondary)] mb-10 max-w-md">
                Your message is on our desk. Expect a personal reply within
                one business day.
              </p>
              <button
                className="btn-outline w-fit"
                onClick={() => setSuccess(false)}
                data-testid="contact-reset-btn"
              >
                Send Another
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-8"
              data-testid="contact-form"
            >
              <div className="grid md:grid-cols-2 gap-8">
                <Field label="Full Name">
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="line-input"
                    placeholder="Your name"
                    required
                    data-testid="contact-input-name"
                  />
                </Field>
                <Field label="Email">
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className="line-input"
                    placeholder="you@email.com"
                    required
                    data-testid="contact-input-email"
                  />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Field label="Phone (optional)">
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="line-input"
                    placeholder="(555) 000-0000"
                    data-testid="contact-input-phone"
                  />
                </Field>
                <Field label="Project Type">
                  <select
                    name="project_type"
                    value={form.project_type}
                    onChange={handleChange}
                    className="line-input appearance-none cursor-pointer"
                    data-testid="contact-input-project-type"
                  >
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-[var(--bg-primary)] text-[var(--text-primary)]">
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Tell Us About The Project">
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className="line-input resize-none"
                  placeholder="Site, scope, timeline, budget range, anything we should know…"
                  required
                  minLength={5}
                  data-testid="contact-input-message"
                />
              </Field>

              <div className="pt-4 flex flex-wrap items-center gap-6">
                <button
                  type="submit"
                  className="btn-solid"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? "Sending…" : "Send Inquiry"}
                  {!submitting && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    >
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  )}
                </button>
                <p className="text-xs text-[var(--text-secondary)] max-w-xs">
                  Your information stays with our team. We reply personally —
                  never automated.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-body text-[10px] uppercase tracking-[0.3em] text-[var(--text-secondary)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoRow({ label, value, href, testid }) {
  const content = (
    <div className="group flex items-start gap-6 py-4 border-b border-white/10">
      <span className="font-body text-[10px] uppercase tracking-[0.3em] text-[var(--text-secondary)] pt-1 w-16 shrink-0">
        {label}
      </span>
      <span className="font-display text-xl md:text-2xl text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors duration-500">
        {value}
      </span>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block">
      {content}
    </a>
  ) : (
    <div data-testid={testid}>{content}</div>
  );
}
